<template>
  <form @submit.prevent="handleSubmit">
    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-field">
          <input-text
            elem-id="soServiceName"
            data-testid="so-services-name-input"
            v-model="formData.so_service_name"
            :label="t('Nome do Serviço')"
            :placeholder="t('Digite o nome do serviço')"
            type="text"
            required
            :validation="validateServiceName"
            fluid
          /><!-- input-text -->
          <small v-if="errors.so_service_name" class="error-message">{{ errors.so_service_name }}</small>
        </div>
      </div>

      <div class="flex-item-half">
        <div class="form-field">
          <SelectList
            elem-id="soServiceCategoryId"
            v-model="formData.so_service_category_id"
            :label="t('Categoria')"
            :itens-select="categoryOptions"
            :placeholder="t('Selecione uma categoria')"
            :auto-select-first="false"
          />
          <small v-if="errors.so_service_category_id" class="error-message">{{ errors.so_service_category_id }}</small>
        </div>
      </div>
    </div>

    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-field">
          <input-currency
            elem-id="soServiceDefaultPrice"
            data-testid="so-services-default-price-input"
            v-model="formData.so_service_default_price"
            :label="t('Preço Padrão')"
            :placeholder="t('Digite o preço')"
          />
          <small v-if="errors.so_service_default_price" class="error-message">{{ errors.so_service_default_price }}</small>
        </div>
      </div>
    </div>

    <div class="form-field">
      <input-rich-text
        elem-id="soServiceDescription"
        data-testid="so-services-description-input"
        v-model="formData.so_service_description"
        :label="t('Descrição')"
        :placeholder="t('Digite a descrição do serviço')"
      />
      <small v-if="errors.so_service_description" class="error-message">{{ errors.so_service_description }}</small>
    </div>

    <div class="form-field">
      <color-picker
        elem-id="soServiceColor"
        v-model="formData.so_service_color"
        :label="t('Cor de exibição')"
      />
    </div>

    <!-- Production Steps Section -->
    <SOProductionStepsPicker
      v-if="props.isEditMode && serviceIdNumber"
      ref="stepsPickerRef"
      :entityId="serviceIdNumber"
      entityType="service"
      elemIdPrefix="soServiceStep"
    />
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import SOProductionStepsPicker from '../so-production-steps/SOProductionStepsPicker.vue';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';
import { permissionsState } from '../../services/permissionsService';
import { currencyMinorUnitsFactor } from '../../utils/currencyMinorUnits';

type Id = number | string;

interface Props {
  serviceId?: Id | null;
  isEditMode?: boolean;
}

interface CategoryOption {
  ID: Id;
  Name: string;
}

interface ServiceData {
  so_service_name?: string;
  so_service_category_id?: Id | null;
  so_service_default_price?: number;
  so_service_description?: string;
  so_service_color?: string;
}

interface StepsPickerInstance {
  saveSteps: () => Promise<void>;
}

interface KVItem {
  value: Id;
  label: string;
}

interface AxiosErrorLike {
  response?: { data?: { error?: string } };
  message?: string;
  stack?: string;
}

const props = withDefaults(defineProps<Props>(), {
  serviceId: null,
  isEditMode: false,
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

// Get i18n composable
const { t } = useI18n();

// Use composables
const { getById: fetchService, save: saveService } = useSOCatalog('services');
const { fetchKV: fetchCategoryKV } = useSOCatalog('service_categories');

// Convert serviceId to number for consistency
const serviceIdNumber = computed<number | null>(() => {
  if (!props.serviceId) return null;
  const num = typeof props.serviceId === 'string' ? parseInt(props.serviceId, 10) : props.serviceId;
  log('SOServicesForm', 'serviceIdNumber', 'Converted ID', { original: props.serviceId, converted: num });
  return num;
});

// Reactive data
const formData = reactive<{
  so_service_name: string;
  so_service_category_id: Id | null;
  so_service_default_price: number | null;
  so_service_description: string;
  so_service_color: string;
}>({
  so_service_name: '',
  so_service_category_id: null,
  so_service_default_price: null,
  so_service_description: '',
  so_service_color: ''
});

const errors = reactive<{
  so_service_name: string;
  so_service_category_id: string;
  so_service_default_price: string;
  so_service_description: string;
}>({
  so_service_name: '',
  so_service_category_id: '',
  so_service_default_price: '',
  so_service_description: ''
});

const isLoading = ref<boolean>(false);
const categoryOptions = ref<CategoryOption[]>([]);
const stepsPickerRef = ref<StepsPickerInstance | null>(null);

// Validation function for InputText component
const validateServiceName = (): boolean => {
  return !!formData.so_service_name && formData.so_service_name.trim() !== '' && formData.so_service_name.length <= 255;
};

// Load category options from KV endpoint
const loadCategoryOptions = async (): Promise<void> => {
  try {
    const kvData = await fetchCategoryKV() as KVItem[];
    log('SOServicesForm', 'loadCategoryOptions', 'KV data loaded', kvData);
    // Convert KV array [{value: id, label: name}, ...] to [{ID: id, Name: name}, ...]
    categoryOptions.value = kvData.map((item: KVItem) => ({
      ID: item.value,
      Name: item.label
    }));
  } catch (err) {
    logError('SOServicesForm', 'loadCategoryOptions', 'Error loading categories', err);
    categoryOptions.value = [];
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!serviceIdNumber.value) {
    logWarn('SOServicesForm', 'loadForEdit', 'No serviceId provided');
    return;
  }

  log('SOServicesForm', 'loadForEdit', 'Loading service for edit', serviceIdNumber.value);
  isLoading.value = true;

  try {
    const data = await fetchService(serviceIdNumber.value) as ServiceData | null;
    log('SOServicesForm', 'loadForEdit', 'Loaded service data', data);

    if (!data) {
      throw new Error('Dados do servico nao encontrados');
    }

    // Populate form. get() returns the RAW cents integer (unlike lista, which
    // divides by 100), so convert back to major units for the InputCurrency.
    const priceFactor = currencyMinorUnitsFactor((permissionsState as { normalizedLocale?: string })?.normalizedLocale);
    formData.so_service_name = data.so_service_name || '';
    formData.so_service_category_id = data.so_service_category_id || null;
    formData.so_service_default_price = (data.so_service_default_price || 0) / priceFactor;
    formData.so_service_description = data.so_service_description || '';
    formData.so_service_color = data.so_service_color || '';

    // Clear errors
    clearErrors();
  } catch (error: unknown) {
    const err = error as AxiosErrorLike;
    logError('SOServicesForm', 'loadForEdit', 'Error loading service', {
      serviceId: serviceIdNumber.value,
      error: err.message,
      stack: err.stack,
      response: err.response
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar serviço') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOServicesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.so_service_name || formData.so_service_name.trim() === '') {
    errors.so_service_name = t('Campo obrigatório');
    isValid = false;
  } else if (formData.so_service_name.length > 255) {
    errors.so_service_name = t('Nome muito longo') + ' (max 255)';
    isValid = false;
  }

  log('SOServicesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.so_service_name = '';
  errors.so_service_category_id = '';
  errors.so_service_default_price = '';
  errors.so_service_description = '';
};

const resetForm = (): void => {
  log('SOServicesForm', 'resetForm', 'Resetting form');
  formData.so_service_name = '';
  formData.so_service_category_id = null;
  formData.so_service_default_price = null;
  formData.so_service_description = '';
  formData.so_service_color = '';
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  log('SOServicesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOServicesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    // Backend stores so_service_default_price in MINOR units (cents); the form
    // holds a major-unit float from InputCurrency. Convert with a minorUnit-aware
    // factor so CLP (factor 1) is not multiplied 100x. Mirrors SOServicePriorities.
    const priceFactor = currencyMinorUnitsFactor((permissionsState as { normalizedLocale?: string })?.normalizedLocale);
    const payload: {
      so_service_name: string;
      so_service_category_id: Id | null;
      so_service_default_price: number;
      so_service_description: string;
      so_service_color: string;
      so_service_id?: number;
    } = {
      so_service_name: formData.so_service_name.trim(),
      so_service_category_id: formData.so_service_category_id,
      so_service_default_price: Math.round((formData.so_service_default_price || 0) * priceFactor),
      so_service_description: formData.so_service_description || '',
      so_service_color: formData.so_service_color || ''
    };

    if (props.isEditMode && serviceIdNumber.value) {
      // Update existing service
      payload.so_service_id = serviceIdNumber.value;
      log('SOServicesForm', 'handleSubmit', 'Updating service', payload);
      await saveService(payload);
      eventBus.emit('globalMessage.success', t('Serviço atualizado com sucesso'));
    } else {
      // Create new service
      log('SOServicesForm', 'handleSubmit', 'Creating service', payload);
      await saveService(payload);
      eventBus.emit('globalMessage.success', t('Serviço criado com sucesso'));
    }

    // Save production steps if in edit mode
    if (props.isEditMode && serviceIdNumber.value && stepsPickerRef.value) {
      await stepsPickerRef.value.saveSteps();
    }

    emit('submit-success');
    resetForm();
  } catch (error: unknown) {
    logError('SOServicesForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar serviço') + ': ' + message
        : t('Erro ao criar serviço') + ': ' + message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in serviceId to reload data
watch(() => props.serviceId, async (newId) => {
  log('SOServicesForm', 'watch', 'serviceId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

// Lifecycle
onMounted(() => {
  log('SOServicesForm', 'mounted', 'Component mounted', {
    serviceId: props.serviceId,
    serviceIdNumber: serviceIdNumber.value,
    isEditMode: props.isEditMode
  });

  // Load category options
  loadCategoryOptions();

  if (props.isEditMode && serviceIdNumber.value) {
    loadForEdit();
  }
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm,
  getServiceName: (): string => formData.so_service_name
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.flex-container {
  display: flex;
  gap: 1rem;
}

.flex-item-half {
  flex: 1;
}

.textarea-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--p-text-color);
  margin-bottom: 0.25rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

</style>
